import React, { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateFAQSchema, generateTownPageSchema } from "@/lib/schema";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";
import { NursingHomeResults } from "@/components/nursing-homes/county/NursingHomeResults";
import { useTownNursingHomes, useTownList } from "@/hooks/use-town-nursing-homes";
import { Input } from "@/components/ui/input";
import { MapPin, Search, ArrowLeft } from "lucide-react";
import { defaultFAQs } from "@/lib/county-faqs";
import { CountyFAQSection } from "@/components/nursing-homes/county/CountyFAQSection";
import { SortingSelect } from "@/components/nursing-homes/county/SortingSelect";
import { useMatchScore, calculateMatchScore } from "@/hooks/use-match-score";

function slugToTitle(slug: string) {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function TownPage() {
  const { countySlug, townSlug } = useParams<{ countySlug: string; townSlug: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const { prefs } = useMatchScore();

  const countyName = slugToTitle(countySlug || "");
  const townName = slugToTitle(townSlug || "");
  const currentYear = new Date().getFullYear();

  const { data: nursingHomes = [], isLoading, error } = useTownNursingHomes(countySlug, townSlug);
  const { data: allTowns = [] } = useTownList(countySlug);
  const otherTowns = allTowns.filter((t) => t.slug !== townSlug);

  const filtered = useMemo(() => {
    let list = searchQuery
      ? nursingHomes.filter((h) =>
          h.center_name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : nursingHomes;

    if (sortBy === "match_score" && prefs) {
      list = [...list].sort((a, b) => {
        const scoreA = calculateMatchScore(a, prefs).score;
        const scoreB = calculateMatchScore(b, prefs).score;
        return scoreB - scoreA;
      });
    } else if (sortBy === "name_desc") {
      list = [...list].sort((a, b) =>
        (b.center_name || "").localeCompare(a.center_name || "")
      );
    } else {
      // Default name_asc (data arrives sorted from DB)
      list = [...list].sort((a, b) =>
        (a.center_name || "").localeCompare(b.center_name || "")
      );
    }
    return list;
  }, [nursingHomes, searchQuery, sortBy, prefs]);


  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Nursing Homes", url: "/nursing-homes" },
    { name: countyName, url: `/nursing-homes/${countySlug}` },
    { name: townName, url: `/nursing-homes/${countySlug}/town/${townSlug}` },
  ];

  const breadcrumbItems = breadcrumbs.map((b) => ({ label: b.name, href: b.url }));

  const pageTitle = `Nursing Homes in ${townName}, ${countyName} (${currentYear}) | Grandpal`;
  const pageDescription = `Find and compare ${nursingHomes.length || ""} nursing homes in ${townName}, ${countyName}. Browse HIQA ratings, Fair Deal approval, pricing and availability. Updated ${currentYear}.`;

  const townFaqs = [
    {
      question: `How many nursing homes are in ${townName}?`,
      answer: `Grandpal lists ${nursingHomes.length} nursing home${nursingHomes.length === 1 ? "" : "s"} in ${townName}, ${countyName}. Use the search above to browse all available options.`,
    },
    {
      question: `Are nursing homes in ${townName} Fair Deal approved?`,
      answer: `Many nursing homes in ${townName} are approved under Ireland's Fair Deal (Nursing Home Support Scheme). You can filter by Fair Deal status on each listing.`,
    },
    ...defaultFAQs.slice(0, 3),
  ];

  const schemas = [
    generateFAQSchema(townFaqs),
    ...generateTownPageSchema({
      townName,
      countyName,
      countySlug: countySlug || "",
      townSlug: townSlug || "",
      nursingHomes,
      breadcrumbs,
    }),
  ];


  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={`https://www.grandpal.co/nursing-homes/${countySlug}/town/${townSlug}`}
        schema={schemas}
      />

      {/* Hero */}
      <div className="bg-grandpal-beige py-10">
        <div className="container">
          <BreadcrumbNavigation items={breadcrumbItems} />
          <h1 className="text-3xl sm:text-4xl font-bold text-center mt-6 mb-3">
            Nursing Homes in {townName} ({currentYear})
          </h1>
          <p className="text-lg text-grandpal-gray text-center max-w-2xl mx-auto">
            Compare {nursingHomes.length > 0 ? nursingHomes.length : ""} nursing home{nursingHomes.length === 1 ? "" : "s"} in {townName}, {countyName} — HIQA ratings, Fair Deal status, pricing and more.
          </p>
        </div>
      </div>

      <div className="container py-10">
        {/* Back link */}
        <Link
          to={`/nursing-homes/${countySlug}`}
          className="inline-flex items-center gap-1.5 text-sm text-grandpal-green hover:underline mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          All nursing homes in {countyName}
        </Link>

        {/* Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search nursing homes in ${townName}…`}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-muted-foreground hidden sm:inline">Sort by:</span>
            <SortingSelect sortBy={sortBy} setSortBy={setSortBy} />
          </div>
        </div>


        {/* Results */}
        <NursingHomeResults
          isLoading={isLoading}
          error={error as Error | null}
          nursingHomes={filtered}
          countyName={townName}
          countySlug={countySlug || ""}
          searchQuery={searchQuery}
        />

        {/* Content / FAQs */}
        <div className="mt-12 space-y-8">
          {/* About section */}
          <div className="bg-white rounded-lg border border-border p-6">
            <h2 className="text-xl font-semibold mb-3 text-grandpal-green-dark flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Nursing Homes in {townName}, {countyName}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {townName} is located in County {countyName}, Ireland. Grandpal lists{" "}
              {nursingHomes.length} nursing home{nursingHomes.length === 1 ? "" : "s"} in this area,
              including both private and HSE-supported facilities. Each listing includes HIQA inspection
              ratings, Fair Deal approval status, care specialisations, and contact details to help
              families make an informed decision.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Use our{" "}
              <Link to="/fair-deal-calculator" className="text-grandpal-green underline underline-offset-2">
                Fair Deal Calculator
              </Link>{" "}
              to estimate your weekly contribution before enquiring, or{" "}
              <Link to="/consultations" className="text-grandpal-green underline underline-offset-2">
                book a free consultation
              </Link>{" "}
              with one of our care advisors.
            </p>
          </div>

          <CountyFAQSection countyName={townName} faqs={townFaqs} />

          {/* Other towns in county — internal linking for SEO */}
          {otherTowns.length > 0 && (
            <div className="bg-white rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold mb-4 text-grandpal-green-dark">
                Other Towns with Nursing Homes in Co. {countyName}
              </h2>
              <ul className="flex flex-wrap gap-2">
                {otherTowns.map((town) => (
                  <li key={town.slug}>
                    <Link
                      to={`/nursing-homes/${countySlug}/town/${town.slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-sm hover:bg-muted hover:border-primary/30 transition-colors"
                    >
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {town.name}
                      <span className="text-xs text-muted-foreground">({town.count})</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Browse all{" "}
                <Link to={`/nursing-homes/${countySlug}`} className="underline underline-offset-2 hover:text-foreground">
                  nursing homes in Co. {countyName}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
